// Shared news article data used by HomePage, NewsPage, and NewsDetailPage

export const NEWS_ARTICLES = [
  {
    slug: "features-of-a-servo-system",
    tag: "Technology",
    date: "June 5, 2024",
    title: "Features of a Servo System",
    excerpt:
      "Precision and efficiency are paramount in industrial applications such as robotics, CNC machinery, and injection molding. Discover how modern servo systems are revolutionizing the manufacturing floor.",
    img: "https://arcuchicnc.com/wp-content/uploads/2024/06/blog-arcuchi-1024x576.jpg",
    author: "Arcuchi Engineering Team",
    readTime: "6 min read",
    body: [
      {
        type: "intro",
        text: "A servo system is a closed-loop control mechanism that uses feedback to precisely control the position, velocity, and torque of a motor. Unlike open-loop systems, servo systems constantly monitor output and correct any deviation from the desired setpoint — making them indispensable in high-precision industrial environments.",
      },
      {
        type: "heading",
        text: "What Makes a Servo System Different?",
      },
      {
        type: "paragraph",
        text: "At the heart of every servo system is the feedback loop. Sensors — typically encoders or resolvers — continuously report the motor's actual position back to the servo drive. The drive compares this to the command signal and instantly compensates for any error. This real-time correction capability gives servo systems their hallmark accuracy.",
      },
      {
        type: "heading",
        text: "Key Features of Modern Servo Systems",
      },
      {
        type: "list",
        items: [
          "High-precision position control — encoders with up to 23-bit resolution ensure repeatability within micrometers.",
          "Fast dynamic response — servo drives can respond to load changes in milliseconds, maintaining consistent output even under variable loads.",
          "Energy efficiency — regenerative braking feeds energy back into the supply bus, reducing overall power consumption by 30–60% compared to hydraulic systems.",
          "Compact footprint — modern servo motors deliver high torque density, replacing bulky hydraulic cylinders with smaller, cleaner electric alternatives.",
          "Programmable motion profiles — acceleration ramps, deceleration curves, and dwell times can all be configured via the drive's software interface.",
          "Built-in diagnostics — fault logging, vibration monitoring, and predictive maintenance alerts reduce unplanned downtime.",
        ],
      },
      {
        type: "heading",
        text: "Applications in Injection Molding",
      },
      {
        type: "paragraph",
        text: "In injection molding machines (IMM), servo systems control the clamping unit, injection unit, and ejector with sub-millimeter precision. The result is consistent shot weight, reduced flash, and shorter cycle times. Arcuchi's AC servo systems are purpose-engineered for IMM retrofits, supporting both hydraulic servo pump drives and direct electric-axis control.",
      },
      {
        type: "quote",
        text: "Our clients typically report cycle-time reductions of 15–25% and energy savings exceeding 40% after retrofitting with Arcuchi servo systems.",
        attribution: "Arcuchi Application Engineering",
      },
      {
        type: "heading",
        text: "Choosing the Right Servo System",
      },
      {
        type: "paragraph",
        text: "When selecting a servo system, consider the load inertia ratio (ideally less than 10:1), the required peak torque versus continuous torque, cable length limitations between drive and motor, and the communication protocol (EtherCAT, CANopen, Modbus, or analog ±10 V). Arcuchi's pre-sales engineering team provides free application reviews to help you select the optimal servo pair for your machine.",
      },
      {
        type: "paragraph",
        text: "Whether you are upgrading an aging hydraulic system or specifying a new machine, a well-chosen servo system delivers a return on investment typically within 12–18 months through energy savings and reduced maintenance costs alone.",
      },
    ],
  },
  {
    slug: "how-to-choose-best-plc-controller",
    tag: "Control Systems",
    date: "May 20, 2024",
    title: "How to Choose the Best PLC Controller for Injection Molding",
    excerpt:
      "Choosing the right controller is crucial for optimizing your injection molding machine's performance and reliability. Learn what to look for.",
    img: "https://arcuchicnc.com/wp-content/uploads/2024/05/How-to-choose-best-PLC-controller-of-plastic-injection-molding-machine-1-1024x538.png",
    author: "Arcuchi Control Systems Team",
    readTime: "7 min read",
    body: [
      {
        type: "intro",
        text: "The PLC (Programmable Logic Controller) is the brain of an injection molding machine. It coordinates every phase of the molding cycle — mold close, injection, pack, cool, open, and eject — while managing alarms, safety interlocks, and real-time process data. Selecting the wrong controller can limit your machine's performance for years to come.",
      },
      {
        type: "heading",
        text: "Dedicated IMM Controller vs. General-Purpose PLC",
      },
      {
        type: "paragraph",
        text: "General-purpose PLCs require extensive custom programming to handle injection molding sequences. A dedicated IMM controller, by contrast, ships with a pre-built molding process library, a purpose-designed operator interface, and pre-wired I/O templates that dramatically reduce commissioning time. For most retrofit projects, a dedicated controller is the better choice.",
      },
      {
        type: "heading",
        text: "Key Selection Criteria",
      },
      {
        type: "list",
        items: [
          "I/O count & type — count your existing solenoid valves, pressure transducers, temperature inputs (thermocouples or RTDs), and encoder channels. Choose a controller with at least 20% spare capacity.",
          "Scan rate — injection processes change in milliseconds. Aim for a controller scan time under 2 ms for the fast I/O module.",
          "Display quality — a 7-inch or larger full-color touchscreen with intuitive icon-based navigation reduces operator errors.",
          "Communication ports — ensure support for RS-232/485 (legacy sensors), Ethernet (MES/SCADA integration), and USB (data logging, program backup).",
          "Temperature control channels — a typical IMM needs 4–16 zones. Verify the controller supports PID auto-tune on all channels simultaneously.",
          "Brand compatibility — confirm the new controller can interface with your existing servo drive, proportional valve amplifier, and safety relay.",
          "Local support & spare parts — choose a supplier with regional offices and guaranteed spare parts availability for at least 10 years.",
        ],
      },
      {
        type: "heading",
        text: "The Arcuchi IMM Controller Advantage",
      },
      {
        type: "paragraph",
        text: "Arcuchi's IMM controllers support machines from 50 to 3,000 tonnes clamping force. The multi-axis control board manages injection, screw rotation, and ejector on a single real-time processor, eliminating the communication latency found in multi-PLC architectures. A built-in mold protection algorithm detects abnormal clamping force within 2 ms, preventing costly mold damage.",
      },
      {
        type: "quote",
        text: "After switching to the Arcuchi IMM controller, we cut our setup time from 4 hours to under 45 minutes per mold change.",
        attribution: "Production Manager, automotive components supplier, Turkey",
      },
      {
        type: "heading",
        text: "Installation & Migration Path",
      },
      {
        type: "paragraph",
        text: "A typical controller retrofit takes 1–3 days including wiring, parameter setup, and trial shots. Arcuchi's engineers provide remote support during commissioning and supply a full electrical schematic library for your machine model. The controller's built-in SPC (Statistical Process Control) module begins recording Cpk data immediately, giving you instant visibility into process stability.",
      },
    ],
  },
  {
    slug: "what-is-hydraulic-ac-servo-system",
    tag: "Hydraulics",
    date: "April 18, 2024",
    title: "What Is a Hydraulic AC Servo System?",
    excerpt:
      "A hydraulic AC servo system uses hydraulic fluid to transmit power and control motion, offering high performance for heavy industrial applications.",
    img: "https://arcuchicnc.com/wp-content/uploads/2024/04/Let-us-show-yu-the-way.-1-1024x538.png",
    author: "Arcuchi Hydraulics Division",
    readTime: "5 min read",
    body: [
      {
        type: "intro",
        text: "A hydraulic AC servo system combines the power density of hydraulics with the precision of electric servo control. Instead of a fixed-speed induction motor driving a constant-pressure pump, a servo motor adjusts its speed and torque in real time to match the exact demand of the hydraulic circuit — eliminating throttling losses and delivering energy only when and where it is needed.",
      },
      {
        type: "heading",
        text: "How It Works",
      },
      {
        type: "paragraph",
        text: "The servo drive receives a flow/pressure demand signal from the machine controller. It commands the servo motor to accelerate or decelerate the fixed-displacement pump accordingly. A pressure transducer closes the loop, ensuring the system holds the setpoint within ±1 bar even as loads change. During idle phases (mold cooling, ejection dwell), the motor slows to near-zero, virtually eliminating stand-by losses.",
      },
      {
        type: "heading",
        text: "Advantages Over Traditional Fixed-Speed Hydraulics",
      },
      {
        type: "list",
        items: [
          "Energy savings of 30–60% — power follows demand rather than being throttled away as heat.",
          "Reduced oil temperature — lower oil temperature extends fluid life and seal life significantly.",
          "Quieter operation — noise levels drop from 75–80 dB to 65–70 dB in idle phases.",
          "Faster response — the servo motor reaches target pressure in 60–100 ms vs. 200–400 ms for proportional valve systems.",
          "Smaller cooling system required — less heat generated means smaller heat exchangers and lower water consumption.",
          "Higher process repeatability — closed-loop pressure control reduces shot-to-shot weight variation.",
        ],
      },
      {
        type: "heading",
        text: "Ideal Applications",
      },
      {
        type: "paragraph",
        text: "Hydraulic AC servo systems are ideal for injection molding machines (especially large-tonnage presses), die-casting machines, hydraulic presses, rubber vulcanizing presses, and blow molding machines — any machine where the hydraulic demand varies significantly across the cycle.",
      },
      {
        type: "quote",
        text: "We measured a 52% reduction in electricity consumption on a 650-tonne IMM after retrofitting with the Arcuchi servo hydraulic system. The payback was under 14 months.",
        attribution: "Plant Engineer, plastics manufacturer, Vietnam",
      },
      {
        type: "heading",
        text: "Retrofit Considerations",
      },
      {
        type: "paragraph",
        text: "Most hydraulic machines can be retrofitted without replacing the hydraulic power unit. The existing induction motor and pump are replaced with a servo motor and fixed-displacement pump, the servo drive is mounted in the electrical cabinet, and the controller I/O is extended to communicate flow and pressure setpoints. Arcuchi provides a complete retrofit kit including motor coupling adapter, drive mounting plate, and wiring harness for common machine brands.",
      },
    ],
  },
  {
    slug: "imm-controller-upgrade-guide",
    tag: "Upgrade Guide",
    date: "March 10, 2024",
    title: "Step-by-Step Guide to Upgrading Your IMM Controller",
    excerpt:
      "Is your injection molding machine running on an outdated controller? Here is a practical guide to planning and executing a successful controller upgrade.",
    img: "https://arcuchicnc.com/wp-content/uploads/2024/06/blog-arcuchi-1024x576.jpg",
    author: "Arcuchi Field Engineering",
    readTime: "8 min read",
    body: [
      {
        type: "intro",
        text: "Older injection molding machines often suffer from discontinued spare parts, unreliable temperature control, and limited data-logging capability. A controller upgrade breathes new life into existing mechanical hardware, extending machine service life by 10–15 years at a fraction of the cost of a new machine.",
      },
      {
        type: "heading",
        text: "Step 1 — Assess Your Current System",
      },
      {
        type: "paragraph",
        text: "Document all existing I/O: count solenoid valves, pressure and temperature inputs, encoder channels, and safety circuits. Photograph the electrical cabinet interior and collect the original electrical schematic. Identify the existing servo drive model and communication protocol (analog, CANopen, or proprietary). This assessment takes half a day and is the foundation of a successful retrofit.",
      },
      {
        type: "heading",
        text: "Step 2 — Select the Replacement Controller",
      },
      {
        type: "paragraph",
        text: "Match I/O count with at least 20% headroom. Confirm display size, temperature zone count, and communication interfaces. For machines above 500 tonnes, verify that the controller supports proportional valve amplifiers and closed-loop pressure feedback. Arcuchi's IMM-A series covers 50–3,000 tonne machines with a single hardware platform.",
      },
      {
        type: "heading",
        text: "Step 3 — Plan the Wiring Migration",
      },
      {
        type: "list",
        items: [
          "Label every wire before disconnecting anything — colour-coded sleeves and a wire log spreadsheet.",
          "Replace all terminal blocks if they are more than 10 years old — corroded connections cause intermittent faults.",
          "Route new controller power supply separately from high-current servo cables to avoid interference.",
          "Install an EMC filter on the incoming supply if the site has variable-frequency drives or welding equipment nearby.",
        ],
      },
      {
        type: "heading",
        text: "Step 4 — Commission and Test",
      },
      {
        type: "paragraph",
        text: "Load machine-specific parameters (clamp force calibration, barrel temperatures, injection pressure limits). Run dry cycles at low speed, checking every solenoid valve and sensor response. Gradually increase speed and pressure, verifying alarm setpoints and safety interlocks. Record baseline cycle time and shot weight before final handover.",
      },
      {
        type: "quote",
        text: "The upgrade paid for itself in 11 months through reduced scrap and eliminated emergency maintenance calls.",
        attribution: "Maintenance Manager, consumer electronics molder, Indonesia",
      },
      {
        type: "heading",
        text: "Step 5 — Training and Documentation",
      },
      {
        type: "paragraph",
        text: "Arcuchi provides on-site operator training (typically 4 hours) covering daily operation, mold change procedure, alarm diagnosis, and parameter backup. A complete as-built electrical schematic is delivered in both PDF and DWG format. Remote access via Ethernet enables Arcuchi engineers to assist with future troubleshooting without a site visit.",
      },
    ],
  },
  {
    slug: "energy-savings-variable-frequency-drives",
    tag: "Energy Efficiency",
    date: "February 14, 2024",
    title: "Cutting Energy Costs with Variable Frequency Drives",
    excerpt:
      "Variable frequency drives (VFDs) can slash pump and fan energy consumption by 20–50%. Here is how to identify the best candidates on your factory floor.",
    img: "https://arcuchicnc.com/wp-content/uploads/2024/05/How-to-choose-best-PLC-controller-of-plastic-injection-molding-machine-1-1024x538.png",
    author: "Arcuchi Energy Solutions",
    readTime: "6 min read",
    body: [
      {
        type: "intro",
        text: "Electric motors account for roughly 70% of industrial electricity consumption. Most of these motors run at fixed speed, even when the process demands only a fraction of full flow or pressure. A variable frequency drive (VFD) solves this by matching motor speed to actual demand, eliminating throttling losses and saving energy proportional to the cube of the speed reduction.",
      },
      {
        type: "heading",
        text: "The Affinity Laws — Why VFDs Save So Much",
      },
      {
        type: "paragraph",
        text: "For centrifugal loads (pumps, fans, blowers), the affinity laws state that power consumed is proportional to the cube of speed. Reducing motor speed by just 20% cuts power consumption by nearly 50%. This means a pump running at 80% speed uses only half the electricity of the same pump at 100% speed — a compelling case for VFD installation on any variable-flow application.",
      },
      {
        type: "heading",
        text: "Best Candidates for VFD Retrofit",
      },
      {
        type: "list",
        items: [
          "Cooling tower fans — load varies with ambient temperature and production volume.",
          "Chilled water pumps — flow can be reduced during low-production periods.",
          "Compressed air system motors — demand-side control reduces unload running time.",
          "Hydraulic power unit fixed-speed motors on machines with long idle phases.",
          "Conveyor drives where speed needs to match upstream/downstream equipment.",
        ],
      },
      {
        type: "heading",
        text: "VFD vs. Servo Drive — Which Do You Need?",
      },
      {
        type: "paragraph",
        text: "A VFD controls speed without positional feedback — suitable for pumps, fans, and conveyors. A servo drive adds closed-loop position and torque control — necessary for injection axes, clamping units, and precision feeders. Arcuchi supplies both: the FC-series VFDs for general variable-speed applications and the AC-series servo drives for precision motion control.",
      },
      {
        type: "quote",
        text: "We installed 14 Arcuchi FC-series VFDs on cooling and pump motors across our plant. Annual electricity savings exceeded USD 38,000 in the first year.",
        attribution: "Utilities Engineer, rubber products manufacturer, Brazil",
      },
      {
        type: "heading",
        text: "Calculating Your Payback Period",
      },
      {
        type: "paragraph",
        text: "To estimate payback: measure current motor kW (clamp meter on all three phases), estimate average load factor (typically 60–80% of nameplate for oversized pumps), apply the affinity law to project savings at reduced speed, and multiply by your electricity tariff. Most VFD installations in industrial settings achieve payback in 12–24 months.",
      },
    ],
  },
  {
    slug: "servo-vs-traditional-hydraulic",
    tag: "Comparison",
    date: "January 22, 2024",
    title: "Servo Systems vs Traditional Hydraulics: Which Is Right for You?",
    excerpt:
      "Both servo electric and traditional hydraulic systems have their place in manufacturing. Understanding the trade-offs helps you make the right investment decision.",
    img: "https://arcuchicnc.com/wp-content/uploads/2024/04/Let-us-show-yu-the-way.-1-1024x538.png",
    author: "Arcuchi Engineering Team",
    readTime: "5 min read",
    body: [
      {
        type: "intro",
        text: "The debate between servo electric and hydraulic drive systems is nuanced. Neither technology is universally superior — the right choice depends on force requirements, precision needs, cycle profile, available utilities, and total cost of ownership over the machine's intended lifespan.",
      },
      {
        type: "heading",
        text: "Force & Power Density",
      },
      {
        type: "paragraph",
        text: "Hydraulics excel at very high force output in a compact package. A small hydraulic cylinder can generate hundreds of tonnes of clamping force. All-electric servo systems can match this on machines up to approximately 500 tonnes, but for very large presses (1,000+ tonnes), hybrid servo-hydraulic systems often offer the best balance of precision and force capacity.",
      },
      {
        type: "heading",
        text: "Precision & Repeatability",
      },
      {
        type: "paragraph",
        text: "Servo electric systems have the clear advantage for precision. Closed-loop position control via high-resolution encoders delivers repeatability in the micrometer range. Hydraulic systems, even with proportional valves and pressure feedback, are affected by oil temperature, viscosity changes, and seal wear — leading to greater shot-to-shot variation over time.",
      },
      {
        type: "heading",
        text: "Operating Cost Comparison",
      },
      {
        type: "list",
        items: [
          "Energy: Servo electric uses 30–60% less energy than fixed-speed hydraulic. Servo hydraulic (variable-speed pump) is 20–40% more efficient than fixed-speed.",
          "Maintenance: Hydraulic systems require oil changes every 2–4 years, filter replacements, seal maintenance, and periodic pump servicing. Electric servo systems are nearly maintenance-free apart from annual drive inspection.",
          "Cleanliness: Hydraulic oil leaks are a contamination risk for food, medical, and electronics applications. Servo electric systems produce no oil contamination.",
          "Noise: Fixed-speed hydraulic systems run at constant noise levels (75–85 dB). Servo systems are significantly quieter, especially during non-productive phases.",
        ],
      },
      {
        type: "heading",
        text: "The Hybrid Servo-Hydraulic Solution",
      },
      {
        type: "paragraph",
        text: "For machines that already have a hydraulic power unit, the servo-hydraulic approach — replacing the fixed-speed motor with an AC servo motor — provides the best return on investment. Existing cylinders, valves, and piping are retained. Only the motor, pump coupling, and drive are replaced. Energy savings of 30–50% are typical, with a payback period of 12–24 months.",
      },
      {
        type: "quote",
        text: "We evaluated full all-electric conversion but the capital cost was three times higher. The Arcuchi servo-hydraulic retrofit gave us 80% of the benefits at 30% of the cost.",
        attribution: "Engineering Director, packaging machinery OEM, Mexico",
      },
      {
        type: "heading",
        text: "Arcuchi's Recommendation",
      },
      {
        type: "paragraph",
        text: "For new machines above 300 tonnes, consider hybrid servo-hydraulic. For new machines below 300 tonnes in cleanroom or precision applications, consider full servo electric. For existing machines with hydraulic systems in good mechanical condition, servo-hydraulic retrofit delivers the fastest ROI. Arcuchi's engineers will conduct a free feasibility assessment and provide a detailed cost-benefit comparison for your specific application.",
      },
    ],
  },
];

export function getArticleBySlug(slug) {
  return NEWS_ARTICLES.find((a) => a.slug === slug) || null;
}
